import React from 'react';
import { TableCell, Typography, Box, Tooltip } from '@mui/material';
import { TSortableColumn } from '../../Components/GenericTable/types';
import EmailIcon from '@mui/icons-material/Email';
import FormattedDate from '../../Components/GenericTable/TableCells/FormattedDate';
import { classes } from '../../Components/GenericTable/styles';

interface EmailBatch {
  id: number;
  email_template: string;
  user: {
    name: string;
    link: string;
  };
  recipients: number;
  delayed_to?: string;
  created_at: string;
  status: string;
}

interface EmailBatchTableRowsProps {
  column: TSortableColumn;
  item: EmailBatch;
  tableHovered: boolean;
  dispatch?: any;
}

const EmailBatchTableRows: React.FC<EmailBatchTableRowsProps> = ({
  column,
  item,
  tableHovered,
  dispatch
}) => {
  if (!column.enabled) return null;

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'sent':
      case 'completed':
        return '#4caf50';
      case 'pending':
      case 'processing':
        return '#ff9800';
      case 'failed':
        return '#f44336';
      default:
        return '#666666';
    }
  };

  switch (column.id) {
    case 'email_template':
      return (
        <TableCell
          key={`${item.id}-${column.id}`}
          sx={{
            ...classes.sticky,
            ...classes.itemTitleTableCell,
            left: '0px',
            ...(tableHovered && classes.scrollShadowVisible),
            cursor: 'pointer',
            minWidth: '180px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={item.status} placement="top" arrow>
              <Box
                sx={{
                  borderRadius: '6px',
                  padding: '6px',
                  backgroundColor: `${getStatusColor(item.status)}33`
                }}
              >
                <EmailIcon sx={{ color: getStatusColor(item.status) }} fontSize="small" />
              </Box>
            </Tooltip>
            <Tooltip title="Click here to see email Analytics" placement="top" arrow>
              <Box
                onClick={() => {
                  dispatch &&
                    dispatch({
                      type: 'SET_SELECTED_ROW_DATA',
                      payload: { name: item.email_template, item: item }
                    });
                }}
                sx={{
                  paddingLeft: '20px',
                  fontWeight: 'bold',
                  color: '#666666',
                  cursor: 'pointer',
                  '& span:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                <span>{item.email_template || '-'}</span>
              </Box>
            </Tooltip>
          </Box>
        </TableCell>
      );
    case 'user':
      return (
        <TableCell key={`${item.id}-${column.id}`}>
          {item?.user?.name ? (
            <a href={item.user.link} target="_blank" rel="noreferrer">
              {item.user.name}
            </a>
          ) : (
            '-'
          )}
        </TableCell>
      );
    case 'recipients':
      return (
        <TableCell key={`${item.id}-${column.id}`} sx={{ textAlign: 'right' }}>
          {item.recipients || '-'}
        </TableCell>
      );
    case 'delayed_to':
      return (
        <TableCell key={`${item.id}-${column.id}`}>{item.delayed_to || 'Delivered'}</TableCell>
      );
    case 'created_at':
      return (
        <TableCell key={`${item.id}-${column.id}`}>
          {item.created_at ? <FormattedDate date={item.created_at} /> : '-'}
        </TableCell>
      );
    default:
      return <TableCell key={`${item.id}-${column.id}`} />;
  }
};

export default EmailBatchTableRows;
